import { forwardRef, useEffect, useRef, useState, type ChangeEvent, type FocusEvent, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { findCountry, formatPhone, normalisePhone, type PhoneCountry, type PhoneReason, type PhoneResult } from '@repletoryreach/sdk-utils/phone';
import { useMergedRef } from '@repletoryreach/sdk-utils/hooks';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';
import { CountryPicker, type CountryPickerLabels } from './CountryPicker.js';
import { usePhoneDefaultCountry } from './PhoneDefaults.js';
import './PhoneInput.css';

export interface PhoneInputLabels extends CountryPickerLabels {
  /** The line under the field once the number reads: receives "+27 61 536 0809". */
  savedAs?: (formatted: string) => ReactNode;
  /** One sentence per refusal reason; the English default is used where a key is missing. */
  reasons?: Partial<Record<PhoneReason, (detail: PhoneRefusalDetail) => ReactNode>>;
}

export interface PhoneRefusalDetail {
  /** The country that was assumed, when there was one. */
  country?: PhoneCountry;
  /** "9" or "8 to 10". */
  expected?: string;
  /** How many digits were there. */
  digits?: number;
}

export interface PhoneChangeMeta {
  /** Whether `e164` is a number the platform will accept. */
  valid: boolean;
  /** ISO 3166-1 alpha-2 the picker sits on, or the number's own country. */
  country: string;
  /** What the person actually typed. */
  raw: string;
  reason?: PhoneReason;
  result: PhoneResult;
}

export interface PhoneInputProps
  extends InputWrapperBaseProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'value' | 'defaultValue' | 'onChange' | 'type'> {
  /** The canonical stored form (`+27615360809`), or empty. Controlled. */
  value?: string;
  defaultValue?: string;
  /** Called on every keystroke with the canonical form (empty while the number does not read) and what was made of the input. */
  onChange?: (e164: string, meta: PhoneChangeMeta) => void;
  /** The country to assume for a number typed without a code. Falls back to the provider, the browser's locale, then South Africa. */
  defaultCountry?: string | null;
  /** Restrict the picker to these countries. */
  countries?: readonly PhoneCountry[];
  labels?: PhoneInputLabels;
  /** Show "Will be saved as …" under the field. Default true. */
  showResult?: boolean;
  /** Name of the hidden field carrying the country, for plain form posts. */
  countryName?: string;
}

const DEFAULT_REASONS: Record<PhoneReason, (d: PhoneRefusalDetail) => ReactNode> = {
  empty: () => 'Enter a phone number.',
  not_a_number: () => 'A phone number has only digits, and may start with +.',
  too_short: (d) => `Too short for ${d.country?.name ?? 'that country'} (+${d.country?.dial ?? ''}): a number there has ${d.expected ?? ''} digits after the country code.`,
  too_long: (d) => `Too long for ${d.country?.name ?? 'that country'} (+${d.country?.dial ?? ''}): a number there has ${d.expected ?? ''} digits after the country code.`,
  unknown_country: () => 'That country code is not one we know. Check the digits after the +.',
  no_country: () => 'Start with + and the country code, for example +27.'
};

/** What the field shows for a stored number: the national part, spaced ("61 536 0809"). */
function nationalDisplay(e164: string): string {
  const r = normalisePhone(e164, null);
  if (!r.ok) return e164;
  return formatPhone(e164).replace(`+${r.dialCode} `, '');
}

/**
 * A phone number field with a country picker beside it.
 *
 * The person types the number the way they would dial it at home
 * (`0615360809`); the field says what it will be saved as
 * (`+27 61 536 0809`) and hands the canonical form to `onChange`. Pasting a
 * full international number (`+44 7700 900123`) moves the picker to that
 * country. A number that cannot be read is explained under the field once
 * the person leaves it, in a sentence that says what was assumed.
 *
 * Copy is in English by default; the consoles pass `labels` from their
 * catalogues.
 */
export const PhoneInput = forwardRef(function PhoneInput(
  props: PhoneInputProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const {
    value, defaultValue, onChange, defaultCountry, countries, labels, showResult = true, countryName,
    label, description, error, required, withAsterisk, size, className, style,
    id, disabled, readOnly, placeholder, onBlur, onFocus, autoComplete = 'tel', inputMode = 'tel',
    ...inputProps
  } = props;

  const fallback = usePhoneDefaultCountry(defaultCountry);
  const initial = value ?? defaultValue ?? '';
  const initialParsed = normalisePhone(initial, fallback);

  const [country, setCountry] = useState<string>(initialParsed.ok ? initialParsed.country : fallback);
  const [raw, setRaw] = useState<string>(initial ? nationalDisplay(initial) : '');
  const [touched, setTouched] = useState(false);
  const lastEmitted = useRef<string>(initialParsed.ok ? initialParsed.e164 : '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mergedRef = useMergedRef(ref, inputRef);

  // A new stored value from outside (a different contact opened in the same
  // drawer) re-seeds the field; our own emissions do not.
  useEffect(() => {
    if (value === undefined) return;
    if (value === lastEmitted.current) return;
    const parsed = normalisePhone(value, fallback);
    setRaw(value ? (parsed.ok ? nationalDisplay(parsed.e164) : value) : '');
    if (parsed.ok) setCountry(parsed.country);
    lastEmitted.current = parsed.ok ? parsed.e164 : '';
    setTouched(false);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // When nothing has been typed yet, follow a late-arriving default (the
  // organisation's setting loads after the first paint).
  useEffect(() => {
    if (!raw && !value && !defaultValue) setCountry(fallback);
  }, [fallback]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = normalisePhone(raw, country);

  const emit = (nextRaw: string, nextCountry: string) => {
    const r = normalisePhone(nextRaw, nextCountry);
    const e164 = r.ok ? r.e164 : '';
    lastEmitted.current = e164;
    onChange?.(e164, {
      valid: r.ok,
      country: r.ok ? r.country : nextCountry,
      raw: nextRaw,
      reason: r.ok ? undefined : r.reason,
      result: r
    });
  };

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.currentTarget.value;
    let nextCountry = country;
    const r = normalisePhone(next, country);
    // A pasted or typed international number moves the picker to its country.
    if (r.ok && r.source === 'input' && r.country !== country) nextCountry = r.country;
    setRaw(next);
    if (nextCountry !== country) setCountry(nextCountry);
    emit(next, nextCountry);
  };

  const onPick = (iso: string) => {
    // Choosing a country while the field holds a full international number
    // keeps only the national part, so the choice takes effect.
    const current = normalisePhone(raw, country);
    const nextRaw = current.ok && current.source === 'input' ? current.national : raw;
    setCountry(iso);
    setRaw(nextRaw);
    emit(nextRaw, iso);
    inputRef.current?.focus();
  };

  const selected = findCountry(country);
  const refusalDetail: PhoneRefusalDetail = !result.ok
    ? { country: findCountry(result.country), expected: result.expected, digits: result.digits }
    : {};
  const reasonText = !result.ok && raw.trim() !== ''
    ? (labels?.reasons?.[result.reason] ?? DEFAULT_REASONS[result.reason])(refusalDetail)
    : null;
  const savedAs = result.ok && showResult
    ? (labels?.savedAs ?? ((f: string) => `Will be saved as ${f}`))(formatPhone(result.e164))
    : null;

  const shownError = error ?? (touched && reasonText ? reasonText : undefined);
  const shownDescription = description || savedAs ? (
    <>
      {description ? <span className="rr-phone__hint">{description}</span> : null}
      {savedAs ? <span className="rr-phone__saved" data-rr-phone-saved>{savedAs}</span> : null}
    </>
  ) : undefined;

  return (
    <InputWrapper
      label={label}
      description={shownDescription}
      error={shownError}
      required={required}
      withAsterisk={withAsterisk}
      size={size}
      className={['rr-phone', className].filter(Boolean).join(' ')}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        /*
         * One control. The outer box carries the design system's input
         * styling (border, radius, height, focus ring, invalid and disabled
         * states) and the two segments inside carry none of their own, so
         * the picker and the number read as a single field with a hairline
         * between them.
         */
        <div
          className={`${ctlClass} rr-phone__control`}
          data-rr-phone-country={selected?.iso}
          data-disabled={disabled || undefined}
          aria-invalid={invalid || undefined}
        >
          <CountryPicker
            variant="compact"
            value={country}
            onChange={onPick}
            countries={countries}
            labels={labels}
            disabled={disabled || readOnly}
            name={countryName}
            onTabOut={() => inputRef.current?.focus()}
          />
          <input
            ref={mergedRef}
            id={inputId}
            type="tel"
            inputMode={inputMode}
            autoComplete={autoComplete}
            className="rr-phone__number"
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            placeholder={placeholder ?? (selected ? nationalPlaceholder(selected) : undefined)}
            value={raw}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            onChange={onInput}
            onFocus={(e: FocusEvent<HTMLInputElement>) => onFocus?.(e)}
            onBlur={(e: FocusEvent<HTMLInputElement>) => { setTouched(true); onBlur?.(e); }}
            {...inputProps}
          />
        </div>
      )}
    </InputWrapper>
  );
});

/** An example the person can copy the shape of: "061 536 0809" under South Africa. */
function nationalPlaceholder(c: PhoneCountry): string {
  const samples: Record<string, string> = {
    ZA: '061 536 0809', NG: '0801 234 5678', KE: '0712 345 678', GB: '07700 900123', US: '(415) 555-2671'
  };
  return samples[c.iso] ?? `${c.trunk}${'1'.repeat(Math.min(c.max, 9))}`.replace(/^$/, '');
}
